import { withBasePath } from '../../basePath';
import { colors } from '@ardoq/design-tokens';
import { I18nLocale, t, Trans } from '@ardoq/i18n';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { InlineLink } from '@ardoq/link';
import { Stack } from '@ardoq/layout';
import { Paragraph, Text } from '@ardoq/typography';
import { EphemeralModalNotificationContent } from '../types';

type ComponentMatrixSlideImageProps = { src: string; alt: string };

const ComponentMatrixSlideImage = ({
  src,
  alt,
}: ComponentMatrixSlideImageProps) => (
  <div
    style={{
      width: '100%',
      height: '500px',
      padding: '30px 20px',
      backgroundColor: colors.grey100,
      borderBottom: `1px solid ${colors.grey200}`,
    }}
  >
    <img
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      alt={alt}
      src={withBasePath(src)}
    />
  </div>
);

export const getComponentMatrixInViewpointModeContent = (
  i18nLocale: I18nLocale
): EphemeralModalNotificationContent => ({
  cards: [
    {
      content: (
        <ComponentMatrixSlideImage
          src="/img/demos/component-matrix-slide-see-where-your-data-connects.png"
          alt={t(
            i18nLocale,
            'ephemeralNotification:componentMatrix.slide1.imageAlt',
            "See where your data connects, and where it doesn't"
          )}
        />
      ),
      header: t(
        i18nLocale,
        'ephemeralNotification:componentMatrix.slide1.header',
        "See where your data connects, and where it doesn't"
      ),
      bodyText: (
        <Stack gap="none">
          <Paragraph variant="text2" color="textModerate">
            {t(
              i18nLocale,
              'ephemeralNotification:componentMatrix.slide1.body',
              'Component matrix answers questions like:'
            )}
          </Paragraph>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            <li>
              <Text variant="text2" color="textModerate">
                {t(
                  i18nLocale,
                  'ephemeralNotification:componentMatrix.slide1.item1',
                  'Which applications are owned by which teams?'
                )}
              </Text>
            </li>
            <li>
              <Text variant="text2" color="textModerate">
                {t(
                  i18nLocale,
                  'ephemeralNotification:componentMatrix.slide1.item2',
                  'Which capabilities have gaps or overlaps?'
                )}
              </Text>
            </li>
            <li>
              <Text variant="text2" color="textModerate">
                {t(
                  i18nLocale,
                  'ephemeralNotification:componentMatrix.slide1.item3',
                  'Where do missing connections exist across your portfolio?'
                )}
              </Text>
            </li>
          </ul>
        </Stack>
      ),
    },
    {
      content: (
        <ComponentMatrixSlideImage
          src="/img/demos/component-matrix-slide-how-it-works.png"
          alt={t(
            i18nLocale,
            'ephemeralNotification:componentMatrix.slide2.imageAlt',
            'How it works'
          )}
        />
      ),
      header: t(
        i18nLocale,
        'ephemeralNotification:componentMatrix.slide2.header',
        'How it works'
      ),
      bodyText: (
        <Paragraph variant="text2" color="textModerate">
          {t(
            i18nLocale,
            'ephemeralNotification:componentMatrix.slide2.body',
            'Pick the components that fill the cells, then choose what defines your rows and columns: a field or a reference. Filled cells show where relationships exist and empty cells reveal the gaps.'
          )}
        </Paragraph>
      ),
    },
    {
      content: (
        <ComponentMatrixSlideImage
          src="/img/demos/component-matrix-slide-build-your-first-matrix.gif"
          alt={t(
            i18nLocale,
            'ephemeralNotification:componentMatrix.slide3.imageAlt',
            'Build your first matrix'
          )}
        />
      ),
      header: t(
        i18nLocale,
        'ephemeralNotification:componentMatrix.slide3.header',
        'Build your first matrix'
      ),
      bodyText: (
        <Paragraph variant="text2" color="textModerate">
          <Trans
            i18nKey="ephemeralNotification:componentMatrix.slide3.body"
            defaults="Need a starting point? <caseStudyLink>Read Component matrix use cases</caseStudyLink> You can reopen this intro from the help menu."
            components={{
              caseStudyLink: (
                // @ts-expect-error Trans supplies children from the translation
                <InlineLink
                  opensInNewTabLabel={t(
                    i18nLocale,
                    'common:labels.opensInNewTab',
                    '(new tab)'
                  )}
                  target="_blank"
                  href={KnowledgeBaseLink.COMPONENT_MATRIX_2}
                />
              ),
            }}
          />
        </Paragraph>
      ),
    },
  ],
  showSkipButton: true,
  endButtonLabel: t(
    i18nLocale,
    'ephemeralNotification:componentMatrix.done',
    'Done'
  ),
  size: 'L',
});
